import React from 'react';
import { Check, ArrowRight } from 'lucide-react';

// Prices and plan names confirmed by the user 2026-08-24 (docs/DECISIONS.md,
// "Pricing page: real plan names/prices/features"); feature lists were left
// to be decided for now ("You decide the list of features for now. Later we
// will revise") - drawn from what the app actually offers/builds toward
// today rather than invented from nothing: Diagnostic/adaptive practice,
// AI Tutor "Lexa", the Progress Tracker's radar/trend/Bloom/difficulty
// charts, branded Performance Reports, Assessments, My Availability, and
// Mentor guidance are all real, shipped features. Smart Study Plan is
// explicitly still unbuilt (docs/DECISIONS.md, "My Availability" decision -
// "smart study plan is not in current scope"), so it's marked "(coming
// soon)" rather than presented as available today.
//
// This app has no billing/payment integration yet, so every tier's CTA
// opens the same Google login modal as the rest of the page rather than
// any checkout flow (docs/DECISIONS.md, 2026-08-07 "Pricing section CTAs").
const TIERS = [
  {
    name: 'Starter',
    price: 'Free',
    period: '',
    features: [
      'Diagnostic test to find your starting level',
      'Adaptive self-practice (30 questions/day)',
      'Instant AI-generated explanations',
      'Basic progress overview',
      'Access to My Syllabus & Learning Journey',
    ],
    highlight: false,
  },
  {
    name: 'Pro',
    currency: '₹',
    price: '599',
    period: '/month',
    features: [
      'Everything in Starter',
      'Unlimited adaptive practice questions',
      'AI Tutor "Lexa" - 24/7 doubt-solving',
      'Advanced Progress Tracker (radar, trend, Bloom-level & difficulty charts)',
      'Branded Performance Reports with date-range filters',
      'Assessments & mock exams',
      'Weekly availability & smart scheduling',
    ],
    highlight: true,
  },
  {
    name: 'Master',
    currency: '₹',
    price: '999',
    period: '/month',
    features: [
      'Everything in Pro',
      'Mentor guidance & progress check-ins',
      'Priority doubt-resolution & support tickets',
      'Personalized Smart Study Plan (coming soon)',
      'Priority customer support',
      'Early access to new features',
    ],
    highlight: false,
  },
];

export default function PricingSection({ isDarkTheme, onLoginClick }) {
  const textColor = isDarkTheme ? 'text-white' : 'text-navy';
  const textMuted = isDarkTheme ? 'text-white/70' : 'text-text-slate';
  const cardBg = isDarkTheme ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200';
  const highlightBg = isDarkTheme ? 'bg-brand-blue/10 border-brand-blue' : 'bg-white border-brand-blue';

  return (
    <section id="pricing" className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-16">
        <h2 className={`font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-4 ${textColor}`}>
          Simple, Transparent
          <span className="block bg-brand-gradient bg-clip-text text-transparent">Pricing</span>
        </h2>
        <p className={`text-lg md:text-xl ${textMuted}`}>Start free, upgrade whenever you're ready</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 items-start">
        {TIERS.map((tier) => (
          <div
            key={tier.name}
            className={`relative rounded-card border-2 ${tier.highlight ? highlightBg : cardBg} p-8 ${tier.highlight ? 'shadow-brand-lg md:-translate-y-3' : ''}`}
          >
            {tier.highlight && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-brand-blue text-white text-xs font-bold">
                Most Popular
              </span>
            )}
            <h3 className={`text-xl font-bold ${textColor} mb-2`}>{tier.name}</h3>
            <p className={`mb-6 ${textColor}`}>
              {/* Fixed 2026-09-02 (real report: "amounts overlapped with
                  Rupee symbols") - "₹599" as one string let Poppins
                  Bold's tight ₹ glyph visually collide with the digit at
                  this size on some devices. Currency symbol is its own span
                  with explicit right margin now, immune to any font's own
                  glyph-spacing regardless of rendering engine. */}
              <span className="text-4xl font-display font-bold">
                {tier.currency && <span className="mr-1">{tier.currency}</span>}
                {tier.price}
              </span>
              <span className={`text-sm ${textMuted}`}>{tier.period}</span>
            </p>
            <ul className="space-y-3 mb-8">
              {tier.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <Check className="text-brand-blue shrink-0 mt-0.5" size={18} />
                  <span className={`text-sm ${textMuted}`}>{feature}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={onLoginClick}
              className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-btn font-semibold transition-all duration-300 ${
                tier.highlight
                  ? 'bg-brand-blue hover:bg-brand-blue-hover text-white shadow-brand-btn'
                  : `border ${isDarkTheme ? 'border-white/20 text-white hover:bg-white/10' : 'border-gray-200 text-navy hover:bg-surface-light'}`
              }`}
            >
              Get Started
              <ArrowRight size={16} />
            </button>
          </div>
        ))}
      </div>

      <p className={`text-center text-sm ${textMuted} mt-10`}>
        Sign in with Google to get started - upgrades are handled after sign-in.
      </p>
    </section>
  );
}
